import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, TrendingUp, TrendingDown, ArrowRight, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import useAuthStore from "@/store/authStore";

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Playfield "Swiss Industrial" tokens (design_guidelines.json) ───
// One accent + one neutral, no rainbow. Heatmap uses a single-hue
// orange sequential ramp; the ramp math lives in `heatBg`/`heatFg`.
export const C = {
  primary: "#F97316",       // orange — accent (in-80% Pareto, current)
  primaryFg: "#FFFFFF",
  data: "#27272A",          // zinc-800 — standard magnitude bars
  border: "#E4E4E7",
  muted: "#71717A",
  fg: "#09090B",
};

export const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString("en-IN");
export const fmtPct = (n) => `${(Number(n) || 0).toFixed(1)}%`;
export const fmtDelta = (n) => (n > 0 ? `+${fmtInt(n)}` : fmtInt(n));

// Single-hue orange sequential ramp for heatmap cells. Light → deep
// as a linear function of value / maxValue. Zero cells stay
// transparent (visually distinct from "small but present").
export const heatBg = (v, max) => {
  if (!v || !max) return "transparent";
  const t = Math.min(1, v / max);
  const lightness = 94 - t * 51; // 94% → 43% (deep orange)
  return `hsl(24 95% ${lightness.toFixed(1)}%)`;
};
export const heatFg = (v, max) => {
  if (!v || !max) return C.muted;
  const t = Math.min(1, v / max);
  return t > 0.55 ? C.primaryFg : C.fg;
};

// Cap for the visible rows on Vertical / Brand split + Pareto lists.
// Matches the backend matrix cap so the whole page reads consistently.
export const VISIBLE_CAP = 8;

// Feb 2026 — third Demand tab. Its own month state, single
// /api/forecast-summary call per change, no cross-talk with the
// Production Summary tab. Forecast-only data — no production, no
// dispatch, no inventory, no BOM, no costing.
//
// Feb 2026 round-2 fixes:
//   1. Brand/vertical resolve off buyer_skus master server-side —
//      "Unassigned brand" collapses to genuine "Unmapped" only.
//   2. SKU rows show the code (buyer_sku_id) in mono; name is muted.
//   3. Vertical × Brand is a heat-shaded matrix with a top-8 cap and
//      an expandable "Others" column (drill-down dialog).
//   4. Vertical/Brand splits + all 3 Pareto cards cap at ~top-80% /
//      ~8 rows with an expandable "Others" row (inline). Palette
//      switched from purple/teal to orange primary + zinc-800.
export default function ForecastSummary() {
  const { token } = useAuthStore();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [othersMatrixOpen, setOthersMatrixOpen] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);

  useEffect(() => {
    if (!month) return;
    let cancelled = false;
    setLoading(true); setError("");
    axios.get(`${API}/api/forecast-summary?month=${month}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (cancelled) return;
      setData(r.data);
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    }).finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [month, token]);

  const isEmpty = data && (data.empty || (data.headline?.total_units || 0) === 0);
  const bridge = useMemo(() => {
    if (!data?.movement) return null;
    const m = data.movement;
    const adds = m.additions.reduce((a, r) => a + (r.units || 0), 0);
    const drops = m.dropouts.reduce((a, r) => a + (r.units || 0), 0);
    return {
      prior: m.prior_total, adds, drops, net: m.net_revisions, curr: m.current_total,
      reconciles: Math.abs((m.prior_total + adds - drops + m.net_revisions) - m.current_total) < 0.01,
    };
  }, [data]);

  // Max cell in the matrix (Others column excluded when computing the
  // ramp so a big tail doesn't wash the rest of the heatmap out).
  const matrixMaxCell = useMemo(() => {
    if (!data?.matrix?.cells?.length) return 0;
    const isOthersLast = data.matrix.brands?.[data.matrix.brands.length - 1] === "Others";
    const stop = isOthersLast ? data.matrix.brands.length - 1 : data.matrix.brands.length;
    let m = 0;
    for (const row of data.matrix.cells) {
      for (let j = 0; j < stop; j++) if (row[j] > m) m = row[j];
    }
    return m;
  }, [data]);

  return (
    <div className="space-y-6" data-testid="forecast-summary-tab">
      {/* Control bar */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <Label className="text-xs uppercase text-zinc-500">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="w-[180px] rounded-sm" data-testid="fs-month-picker" />
        </div>
        <div className="text-right text-xs text-zinc-500">
          <div data-testid="fs-cadence-caption">as of today 06:00 IST</div>
          {data?.movement?.current_snapshot_date && (
            <div>Latest week-close: <span className="font-mono">{data.movement.current_snapshot_date}</span>
              {data.movement.current_is_provisional && (
                <span className="ml-1 inline-block px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 text-[10px]" data-testid="fs-provisional-badge">provisional / WTD</span>
              )}
              {data.movement.prior_snapshot_date && (
                <> · Prior: <span className="font-mono">{data.movement.prior_snapshot_date}</span></>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-center py-12 text-zinc-500"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading…</div>}
      {error && <div className="border border-rose-300 bg-rose-50 text-rose-900 rounded-sm p-3 text-sm" data-testid="fs-error">{error}</div>}

      {!loading && data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Card className="md:col-span-2 rounded-sm" data-testid="fs-headline-total">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-zinc-500">Total forecasted units — {month}</CardTitle></CardHeader>
              <CardContent>
                <div className="text-4xl font-black tracking-tight" data-testid="fs-total-units">{fmtInt(data.headline.total_units)}</div>
                {bridge && bridge.prior > 0 && (
                  <div className={`text-sm mt-1 ${bridge.curr >= bridge.prior ? "text-emerald-700" : "text-rose-700"}`}>
                    {bridge.curr >= bridge.prior ? (
                      <TrendingUp className="inline w-3.5 h-3.5 mr-1" />
                    ) : (
                      <TrendingDown className="inline w-3.5 h-3.5 mr-1" />
                    )}
                    {fmtDelta(bridge.curr - bridge.prior)} vs last week
                  </div>
                )}
              </CardContent>
            </Card>
            {[
              { label: "Active SKUs", value: data.headline.active_skus, tid: "fs-kpi-skus" },
              { label: "Verticals", value: data.headline.verticals, tid: "fs-kpi-verticals" },
              { label: "Brands", value: data.headline.brands, tid: "fs-kpi-brands" },
              { label: "Forecast lines", value: data.headline.lines, sub: `${data.headline.customers} customers`, tid: "fs-kpi-lines" },
            ].map((t) => (
              <Card key={t.label} className="rounded-sm" data-testid={t.tid}>
                <CardHeader className="pb-2"><CardTitle className="text-xs uppercase text-zinc-500">{t.label}</CardTitle></CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tracking-tight">{fmtInt(t.value)}</div>
                  {t.sub && <div className="text-[11px] text-zinc-500">{t.sub}</div>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Unmapped surfacing — genuine data-hygiene gap, not the
              old silent bucket. Kept as a small line, not a chart. */}
          {data.unmapped && data.unmapped.sku_count > 0 && (
            <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 text-xs flex items-center justify-between" data-testid="fs-unmapped-line">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700" />
                <span className="text-amber-900">
                  Unmapped: <span className="font-mono font-semibold">{data.unmapped.sku_count}</span> SKUs
                  {" / "}<span className="font-mono font-semibold">{fmtInt(data.unmapped.units)}</span> units
                  {" "}(<span className="font-mono">{fmtPct(data.unmapped.pct)}</span> of total) — brand/vertical could not be resolved off the buyer-SKU master.
                </span>
              </div>
              {data.unmapped.sample_ids?.length > 0 && (
                <button className="text-amber-900 underline hover:text-amber-950" onClick={() => setUnmappedOpen(true)} data-testid="fs-unmapped-view">
                  View sample →
                </button>
              )}
            </div>
          )}

          {isEmpty && (
            <Card className="rounded-sm"><CardContent className="py-12 text-center text-zinc-500" data-testid="fs-empty-state">
              No forecasts for this month yet.
            </CardContent></Card>
          )}

          {!isEmpty && (
            <>
              {/* Section A — live */}
              <div>
                <h2 className="text-lg font-bold tracking-tight mb-2">Month Snapshot <span className="text-xs text-zinc-500 font-normal">(live)</span></h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SplitCard title="Vertical split" rows={data.vertical_split} total={data.headline.total_units} tid="fs-vertical-split" barColor={C.data} />
                  <SplitCard title="Brand split" rows={data.brand_split} total={data.headline.total_units} tid="fs-brand-split" barColor={C.data} />
                </div>

                {/* Matrix — heat-shaded, top-N + Others */}
                <Card className="mt-4 rounded-sm" data-testid="fs-matrix">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Vertical × Brand</CardTitle>
                    <div className="text-[11px] text-zinc-500">Shaded by unit concentration. Top {VISIBLE_CAP} brands shown; the tail is folded into <span className="font-medium">Others</span> — click the header to expand.</div>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="text-xs border border-zinc-200 border-collapse w-full">
                      <thead>
                        <tr className="bg-zinc-50">
                          <th className="text-left px-2 py-1 border border-zinc-200 uppercase tracking-wide text-[10px] text-zinc-500">Vertical \ Brand</th>
                          {data.matrix.brands.map((b) => {
                            const isOthers = b === "Others";
                            return (
                              <th key={b} className={`px-2 py-1 border border-zinc-200 text-right uppercase tracking-wide text-[10px] ${isOthers ? "cursor-pointer hover:bg-zinc-100 text-orange-700 underline" : "text-zinc-500"}`}
                                  onClick={isOthers ? () => setOthersMatrixOpen(true) : undefined}
                                  data-testid={isOthers ? "fs-matrix-others-header" : undefined}
                              >{b}{isOthers && (data.matrix.others_brands?.length || 0) > 0 && <> <span className="font-normal">({data.matrix.others_brands.length})</span></>}</th>
                            );
                          })}
                          <th className="px-2 py-1 border border-zinc-200 text-right uppercase tracking-wide text-[10px] text-zinc-500 bg-zinc-100">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.matrix.verticals.map((v, i) => (
                          <tr key={v}>
                            <td className="px-2 py-1 border border-zinc-200 font-medium">{v}</td>
                            {data.matrix.brands.map((b, j) => {
                              const cell = data.matrix.cells[i][j];
                              const isOthers = b === "Others";
                              return (
                                <td key={b} className="px-2 py-1 border border-zinc-200 text-right font-mono"
                                    style={{
                                      background: isOthers ? "#F4F4F5" : heatBg(cell, matrixMaxCell),
                                      color: isOthers ? C.fg : heatFg(cell, matrixMaxCell),
                                    }}>
                                  {cell > 0 ? fmtInt(cell) : ""}
                                </td>
                              );
                            })}
                            <td className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold bg-zinc-50">{fmtInt(data.matrix.row_totals[i])}</td>
                          </tr>
                        ))}
                        <tr className="bg-zinc-100">
                          <td className="px-2 py-1 border border-zinc-200 font-bold uppercase tracking-wide text-[10px]">Total</td>
                          {data.matrix.col_totals.map((c, j) => (
                            <td key={j} className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold">{fmtInt(c)}</td>
                          ))}
                          <td className="px-2 py-1 border border-zinc-200 text-right font-mono font-bold">{fmtInt(data.headline.total_units)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Pareto trio */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <ParetoCard title="Top 80% — Vertical" data={data.pareto.vertical} tid="fs-pareto-vertical" mono={false} />
                  <ParetoCard title="Top 80% — Brand" data={data.pareto.brand} tid="fs-pareto-brand" mono={false} />
                  <ParetoCard title="Top 80% — SKU" data={data.pareto.sku} tid="fs-pareto-sku" mono={true} />
                </div>
              </div>

              {/* Section B — snapshot */}
              <div>
                <h2 className="text-lg font-bold tracking-tight mb-2">Week-over-Week Movement
                  <span className="text-xs text-zinc-500 font-normal ml-2">
                    {data.movement.current_snapshot_date
                      ? <>As of <span className="font-mono">{data.movement.current_snapshot_date}</span>
                          {data.movement.prior_snapshot_date && <> vs <span className="font-mono">{data.movement.prior_snapshot_date}</span></>}</>
                      : "No snapshots yet"}
                  </span>
                </h2>
                {!data.movement.current_snapshot_date ? (
                  <Card className="rounded-sm"><CardContent className="py-8 text-center text-zinc-500" data-testid="fs-no-snapshot">
                    No snapshots yet — the first snapshot for this month will be captured next Monday.
                  </CardContent></Card>
                ) : !data.movement.prior_snapshot_date ? (
                  <Card className="rounded-sm"><CardContent className="py-6 text-center text-zinc-500" data-testid="fs-single-snapshot">
                    Baseline captured on <span className="font-mono">{data.movement.current_snapshot_date}</span> — no prior week to compare yet.
                  </CardContent></Card>
                ) : (
                  <>
                    {/* Bridge */}
                    <Card className="mb-3 rounded-sm" data-testid="fs-bridge">
                      <CardContent className="pt-4 grid grid-cols-5 gap-2 text-center text-sm">
                        {[
                          { l: "Last week", v: bridge?.prior, sign: "" },
                          { l: "+ New", v: bridge?.adds, sign: "+" },
                          { l: "− Dropouts", v: bridge?.drops, sign: "−" },
                          { l: "± Revisions", v: bridge?.net, sign: "±" },
                          { l: "Now", v: bridge?.curr, sign: "" },
                        ].map((c) => (
                          <div key={c.l}>
                            <div className="text-[10px] uppercase text-zinc-500">{c.l}</div>
                            <div className="font-mono font-semibold">{c.sign}{fmtInt(c.v || 0)}</div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    {/* Trajectory */}
                    <Card className="mb-3 rounded-sm" data-testid="fs-trajectory">
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly trajectory</CardTitle></CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={180}>
                          <LineChart data={data.trajectory}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                            <XAxis dataKey="snapshot_date" /><YAxis tickFormatter={fmtInt} />
                            <Tooltip formatter={(v) => fmtInt(v)} />
                            <Line type="monotone" dataKey="total_units" stroke={C.primary} strokeWidth={2} dot={{ r: 3, fill: C.primary }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                    {/* Additions / Dropouts / Movers */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <MoverTable title="Additions" rows={data.movement.additions} valKey="units" tid="fs-additions" sign="+" icon={TrendingUp} tone="text-emerald-700" />
                      <MoverTable title="Dropouts" rows={data.movement.dropouts} valKey="units" tid="fs-dropouts" sign="−" icon={TrendingDown} tone="text-rose-700" />
                      <MoverTable title="Major movers" rows={data.movement.movers} valKey="delta" tid="fs-movers" isDelta />
                    </div>
                    {Math.abs(data.movement.other_revisions_net) > 0.01 && (
                      <div className="text-[11px] text-zinc-500 text-right mt-1">
                        Other revisions, net: <span className="font-mono">{fmtDelta(data.movement.other_revisions_net)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Others drill-down — Vertical × Brand matrix */}
      <Dialog open={othersMatrixOpen} onOpenChange={setOthersMatrixOpen}>
        <DialogContent className="max-w-lg" data-testid="fs-matrix-others-dialog">
          <DialogHeader><DialogTitle>Others — brand breakdown</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">Brands folded into the &quot;Others&quot; column (tail of the top-{VISIBLE_CAP} cap). All totals still reconcile.</div>
          <div className="max-h-96 overflow-y-auto border border-zinc-200 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Brand</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Units</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">% of total</th>
                </tr>
              </thead>
              <tbody>
                {(data?.matrix?.others_brands || []).map((b) => (
                  <tr key={b.name} className="border-t border-zinc-200">
                    <td className="px-3 py-1.5">{b.name}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtInt(b.units)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtPct(b.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unmapped sample list */}
      <Dialog open={unmappedOpen} onOpenChange={setUnmappedOpen}>
        <DialogContent className="max-w-lg" data-testid="fs-unmapped-dialog">
          <DialogHeader><DialogTitle>Unmapped buyer SKUs — cleanup list</DialogTitle></DialogHeader>
          <div className="text-xs text-zinc-500 mb-2">These buyer SKUs have forecasts but no brand/vertical resolvable off the master. Fix the buyer-SKU record so future forecasts categorise correctly.</div>
          <div className="max-h-96 overflow-y-auto border border-zinc-200 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Buyer SKU</th>
                  <th className="text-right px-3 py-2 uppercase tracking-wide text-[10px] text-zinc-500">Units</th>
                </tr>
              </thead>
              <tbody>
                {(data?.unmapped?.sample_ids || []).map((r) => (
                  <tr key={r.sku_id} className="border-t border-zinc-200">
                    <td className="px-3 py-1.5 font-mono">{r.sku_id}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtInt(r.units)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Vertical / Brand split card ───────────────────────────────────
// Top-8 rows go on the chart; the tail collapses into an "Others"
// bar that expands inline to reveal the full list.
export function SplitCard({ title, rows, total, tid, barColor }) {
  const [expanded, setExpanded] = useState(false);
  const { visible, tail, tailUnits, tailPct } = useMemo(() => {
    const sorted = [...(rows || [])].sort((a, b) => b.units - a.units);
    const vis = sorted.slice(0, VISIBLE_CAP);
    const t = sorted.slice(VISIBLE_CAP);
    const tu = t.reduce((s, r) => s + (r.units || 0), 0);
    const tp = total ? (tu / total) * 100 : 0;
    return { visible: vis, tail: t, tailUnits: tu, tailPct: tp };
  }, [rows, total]);

  const chartData = tail.length > 0
    ? [...visible, { name: "Others", units: tailUnits, pct: tailPct, __isOthers: true }]
    : visible;

  return (
    <Card className="rounded-sm" data-testid={tid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 30)}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis type="number" tickFormatter={fmtInt} />
            <YAxis type="category" dataKey="name" width={110} interval={0} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmtInt(v)} />
            <Bar dataKey="units" fill={barColor} />
          </BarChart>
        </ResponsiveContainer>
        {tail.length > 0 && (
          <div className="mt-2 border-t border-zinc-200 pt-2">
            <button
              className="text-xs w-full flex items-center justify-between text-orange-700 hover:text-orange-800"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`${tid}-others-toggle`}
            >
              <span className="flex items-center gap-1">
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Others ({tail.length}) — {fmtPct(tailPct)}
              </span>
              <span className="font-mono">{fmtInt(tailUnits)}</span>
            </button>
            {expanded && (
              <ol className="mt-1 text-xs space-y-0.5" data-testid={`${tid}-others-list`}>
                {tail.map((r) => (
                  <li key={r.name} className="flex justify-between py-0.5 border-b border-zinc-100">
                    <span className="truncate text-zinc-600">{r.name}</span>
                    <span className="font-mono text-zinc-600 ml-2">{fmtInt(r.units)} <span className="text-zinc-400">({fmtPct(r.pct)})</span></span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Pareto card ────────────────────────────────────────────────────
// Shows only the top-80% items; the tail collapses into an inline,
// expandable "Others" row so the whole distribution is one click away.
export function ParetoCard({ title, data, tid, mono }) {
  const [expanded, setExpanded] = useState(false);
  const items = data?.items || [];
  const top80 = data?.top80_count || 0;
  const visible = items.slice(0, top80);
  const tail = items.slice(top80);
  const tailUnits = tail.reduce((s, r) => s + (r.units || 0), 0);
  const tailPct = tail.reduce((s, r) => s + (r.pct || 0), 0);

  return (
    <Card className="rounded-sm" data-testid={tid}>
      <CardHeader className="pb-1">
        <CardTitle className="text-xs uppercase tracking-widest text-zinc-500">
          {title}
          <span className="ml-1 font-normal normal-case">({top80} of {data?.total_count || 0})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="text-xs space-y-0.5">
          {visible.map((it, i) => (
            <li key={(it.sku_id || it.name) + i} className="flex justify-between py-0.5 border-b border-zinc-100">
              <span className="truncate flex-1 min-w-0" title={it.description || it.name}>
                <span className="text-zinc-400 mr-1">{i + 1}.</span>
                <span className={mono ? "font-mono font-medium" : "font-medium"}>{mono ? (it.sku_id || it.name) : it.name}</span>
                {mono && it.description && it.description !== it.sku_id && (
                  <span className="text-zinc-400 ml-1 truncate">· {it.description}</span>
                )}
              </span>
              <span className="font-mono ml-2 shrink-0">{fmtPct(it.pct)}</span>
            </li>
          ))}
        </ol>
        {tail.length > 0 && (
          <div className="mt-1 border-t border-zinc-200 pt-1">
            <button
              className="text-xs w-full flex items-center justify-between text-orange-700 hover:text-orange-800"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`${tid}-others-toggle`}
            >
              <span className="flex items-center gap-1">
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Others ({tail.length}) — {fmtPct(tailPct)}
              </span>
              <span className="font-mono">{fmtInt(tailUnits)}</span>
            </button>
            {expanded && (
              <ol className="mt-1 text-xs space-y-0.5" data-testid={`${tid}-others-list`}>
                {tail.map((it, i) => (
                  <li key={(it.sku_id || it.name) + "-tail-" + i} className="flex justify-between py-0.5 border-b border-zinc-100">
                    <span className="truncate flex-1 min-w-0 text-zinc-600">
                      <span className={mono ? "font-mono" : ""}>{mono ? (it.sku_id || it.name) : it.name}</span>
                      {mono && it.description && it.description !== it.sku_id && (
                        <span className="text-zinc-400 ml-1">· {it.description}</span>
                      )}
                    </span>
                    <span className="font-mono ml-2 text-zinc-600 shrink-0">{fmtPct(it.pct)}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
        {top80 > 0 && tail.length > 0 && (
          <div className="text-[10px] text-orange-700 mt-1 border-t border-zinc-200 pt-1">80% reached — {top80} items</div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Mover table ────────────────────────────────────────────────────
// SKU code is the primary identity (mono), description is muted
// secondary. Reads better on the eye and gives a stable join key.
function MoverTable({ title, rows, valKey, tid, sign = "", tone = "", isDelta = false }) {
  return (
    <Card className="rounded-sm" data-testid={tid}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title} <Badge variant="outline" className="text-[10px] ml-1 rounded-sm">{rows.length}</Badge></CardTitle></CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-xs text-zinc-400 italic">None.</div>
        ) : (
          <ol className="text-xs space-y-1">
            {rows.slice(0, 10).map((r) => (
              <li key={r.sku_id} className="flex justify-between gap-2 border-b border-zinc-100 pb-1">
                <span className="truncate flex-1 min-w-0" title={r.description}>
                  <span className="font-mono font-semibold">{r.sku_id}</span>
                  {r.description && r.description !== r.sku_id && (
                    <span className="text-zinc-400 ml-1">· {r.description}</span>
                  )}
                </span>
                <span className={`font-mono shrink-0 ${tone}`}>
                  {isDelta ? (
                    <>{r.delta > 0 ? <ArrowRight className="inline w-3 h-3 rotate-[-45deg] text-emerald-700" /> : <ArrowRight className="inline w-3 h-3 rotate-[45deg] text-rose-700" />}
                    {" "}{fmtDelta(r.delta)}</>
                  ) : (
                    <>{sign}{fmtInt(r[valKey])}</>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
